import { cn, Input } from '@up2stay/up2promo-ui';
import { Search, X } from 'lucide-react';
import type { ChangeEventHandler, ComponentProps, Ref } from 'react';

import { listToolbarSearchInputClass } from '@/core/components/layout/list-toolbar-styles';

export type ListSearchFieldProps = {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  /** Accessible name; defaults to placeholder. */
  'aria-label'?: string;
  /**
   * `plain` — Locations-style compact field on the page canvas.
   * `toolbar` — Products/Leads nested toolbar input (concentric card radius).
   */
  variant?: 'plain' | 'toolbar';
  className?: string;
  inputClassName?: string;
  type?: ComponentProps<typeof Input>['type'];
  id?: string;
  /** Clears the field when provided and value is non-empty. */
  onClear?: () => void;
  clearAriaLabel?: string;
  /** Lets a toggle focus the field when it opens it. */
  inputRef?: Ref<HTMLInputElement>;
};

/**
 * Search field with leading icon — shared list-page toolbar control.
 */
export function ListSearchField({
  value,
  onChange,
  placeholder,
  'aria-label': ariaLabel,
  variant = 'plain',
  className,
  inputClassName,
  type = 'text',
  id,
  onClear,
  clearAriaLabel = 'Clear search',
  inputRef,
}: ListSearchFieldProps) {
  const isToolbar = variant === 'toolbar';
  const showClear = !!onClear && value.length > 0;

  return (
    <div className={cn('relative min-w-0', className)}>
      <Search
        className={cn(
          'pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground',
          isToolbar ? 'left-3.5 text-muted-foreground/80' : 'left-3',
        )}
        aria-hidden
      />
      <Input
        ref={inputRef}
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        className={cn(
          isToolbar ? listToolbarSearchInputClass : 'h-9 bg-card pl-9 text-sm',
          showClear && 'pr-9',
          inputClassName,
        )}
      />
      {showClear ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearAriaLabel}
          className={cn(
            'absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            isToolbar ? 'right-1.5' : 'right-1',
          )}
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
